/**
 * Connection Reference Types
 *
 * Based on LogicAppsUX patterns for Standard SKU workflows.
 * Connections are stored as references, and nodes map to reference keys.
 */

// =============================================================================
// CONNECTION REFERENCE
// =============================================================================

/**
 * A connection reference that points to an actual connection resource.
 * Stored at the workflow level, referenced by operations via ReferenceKey.
 */
export interface ConnectionReference {
  /** API/Connector resource ID */
  api: { id: string };
  /** Connection resource ID */
  connection: { id: string };
  /** Display name of the connection */
  connectionName?: string;
  /** Additional connection properties (varies by connector type) */
  connectionProperties?: Record<string, unknown>;
  /** Runtime URL for the connection (Standard SKU) */
  connectionRuntimeUrl?: string;
  /** Authentication configuration */
  authentication?: ApiHubAuthentication;
  /** Impersonation settings (for triggers that run as invoker) */
  impersonation?: Impersonation;
  /**
   * Runtime source for the connection.
   * 'Dynamic' indicates this is a dynamic connection (oBO/On-Behalf-Of)
   */
  runtimeSource?: ConnectionRuntimeSource;
  /**
   * Proxy URL for dynamic connection calls.
   * Used with dynamic/oBO connections for runtime resolution.
   */
  dynamicConnectionProxyUrl?: string;
}

/**
 * Connection runtime source type
 */
export const ConnectionRuntimeSource = {
  /** Standard connection resolved at design time */
  Static: 'Static',
  /** Dynamic connection resolved at runtime (oBO/On-Behalf-Of) */
  Dynamic: 'Dynamic',
} as const;
export type ConnectionRuntimeSource = (typeof ConnectionRuntimeSource)[keyof typeof ConnectionRuntimeSource];

/**
 * API Hub authentication configuration
 */
export interface ApiHubAuthentication {
  /** Authentication type (e.g., 'ManagedServiceIdentity', 'Raw') */
  type: string;
  /** Identity resource ID (for managed identity auth) */
  identity?: string;
  /** Target audience/resource URI for token acquisition (required by runtime for MI/OAuth) */
  audience?: string;
}

/**
 * Impersonation settings for running as the invoking user
 */
export interface Impersonation {
  /** Source of impersonation identity */
  source?: ImpersonationSource;
  /** Object ID of the identity to impersonate */
  objectId?: string;
}

export const ImpersonationSource = {
  Invoker: 'invoker',
} as const;
export type ImpersonationSource = (typeof ImpersonationSource)[keyof typeof ImpersonationSource];

// =============================================================================
// CONNECTION MAPPING
// =============================================================================

/** Key used to reference a connection in ConnectionReferences */
export type ReferenceKey = string;

/** Node ID in the workflow */
export type NodeId = string;

/** Map of reference keys to connection references (workflow-level) */
export type ConnectionReferences = Record<ReferenceKey, ConnectionReference>;

/** Map of node IDs to reference keys (node-level) */
export type ConnectionMapping = Record<NodeId, ReferenceKey | null>;

// =============================================================================
// CONNECTION TYPES
// =============================================================================

export const ConnectionType = {
  /** Azure Function connection */
  Function: 'function',
  /** Service Provider connection (e.g., Service Bus, Event Hubs) */
  ServiceProvider: 'serviceprovider',
  /** API Management connection */
  ApiManagement: 'apimanagement',
  /** Standard API connection (connectors) */
  ApiConnection: 'apiconnection',
  /** Agent connection */
  Agent: 'agent',
  /** MCP (Model Context Protocol) connection */
  Mcp: 'mcp',
} as const;
export type ConnectionType = (typeof ConnectionType)[keyof typeof ConnectionType];

export const ConnectionReferenceKeyFormat = {
  ApiConnection: 'apiconnection',
  ApiManagement: 'apimanagement',
  Function: 'function',
  OpenApi: 'openapi',
  ServiceProvider: 'serviceprovider',
  HybridTrigger: 'hybridtrigger',
  AgentConnection: 'agentconnection',
  McpConnection: 'mcpconnection',
} as const;
export type ConnectionReferenceKeyFormat =
  (typeof ConnectionReferenceKeyFormat)[keyof typeof ConnectionReferenceKeyFormat];

// =============================================================================
// CONNECTION PARAMETER TYPES
// =============================================================================

export const ConnectionParameterTypes = {
  array: 'array',
  bool: 'bool',
  clientCertificate: 'clientCertificate',
  connection: 'connection',
  gatewaySetting: 'gatewaySetting',
  int: 'int',
  managedIdentity: 'managedIdentity',
  oauthSetting: 'oauthSetting',
  object: 'object',
  secureObject: 'secureObject',
  secureString: 'secureString',
  string: 'string',
} as const;
export type ConnectionParameterTypes =
  (typeof ConnectionParameterTypes)[keyof typeof ConnectionParameterTypes];

/**
 * Connection parameter definition
 */
export interface ConnectionParameter {
  /** Parameter type */
  type: ConnectionParameterTypes;
  /** UI definition for parameter display */
  uiDefinition?: ConnectionParameterUIDefinition;
  /** Managed identity settings if applicable */
  managedIdentitySettings?: ManagedIdentitySetting;
  /** OAuth settings if applicable */
  oAuthSettings?: OAuthSetting;
  /** Gateway settings if applicable */
  gateway?: GatewaySetting;
  /** Source of parameter value */
  parameterSource?: ConnectionParameterSource;
}

export interface ConnectionParameterUIDefinition {
  /** Display name for the parameter */
  displayName?: string;
  /** Description of the parameter */
  description?: string;
  /** Tooltip text */
  tooltip?: string;
  /** Constraints on the parameter */
  constraints?: ConnectionParameterConstraints;
}

export interface ConnectionParameterConstraints {
  /** Whether the parameter is required */
  required?: 'true' | 'false';
  /** Whether the parameter should be hidden */
  hidden?: 'true' | 'false';
  /** Allowed values for the parameter */
  allowedValues?: ConnectionParameterAllowedValue[];
  /** Required capabilities for this parameter */
  capability?: string[];
  /** Tab index for UI ordering */
  tabIndex?: number;
  /** Default value */
  default?: unknown;
}

export interface ConnectionParameterAllowedValue {
  /** Display text */
  text?: string;
  /** Actual value */
  value: unknown;
}

export const ConnectionParameterSource = {
  AppConfiguration: 'AppConfiguration',
} as const;
export type ConnectionParameterSource =
  (typeof ConnectionParameterSource)[keyof typeof ConnectionParameterSource];

// =============================================================================
// AUTHENTICATION TYPES
// =============================================================================

export interface OAuthSetting {
  /** OAuth client ID */
  clientId: string;
  /** Identity provider (e.g., 'aad', 'google') */
  identityProvider: string;
  /** Required scopes */
  scopes: string[];
  /** Redirect mode */
  redirectMode?: string;
  /** Redirect URL after auth */
  redirectUrl: string;
  /** Additional OAuth properties */
  properties: OAuthSettingProperties;
  /** Custom parameters for specific providers */
  customParameters?: Record<string, unknown>;
}

export interface OAuthSettingProperties {
  /** Azure AD resource ID */
  AzureActiveDirectoryResourceId?: string;
  /** Whether this is a first-party Microsoft app */
  IsFirstParty: string;
}

export interface ManagedIdentitySetting {
  /** Target resource URI */
  resourceUri: string;
  /** Additional resource URIs that need access */
  additionalResourceUris?: string[];
  /** Required Azure RBAC roles */
  requiredRoles?: string[];
}

export interface GatewaySetting {
  /** Data source type */
  dataSourceType?: string;
  /** Connection details */
  connectionDetails?: string[];
}

// =============================================================================
// CONNECTION METADATA
// =============================================================================

/**
 * Connection metadata from operation manifest
 */
export interface ConnectionMetadata {
  /** Whether a connection is required for this operation */
  required: boolean;
  /** Type of connection */
  type?: ConnectionType;
  /** Custom connection creation client */
  connectionCreationClient?: string;
  /** Disable automatic connection selection */
  disableAutoSelection?: boolean;
}

// =============================================================================
// CONNECTION STATE (for UI/Store)
// =============================================================================

/**
 * Connection state for a node in the editor
 */
export interface NodeConnectionState {
  /** Reference key pointing to the connection */
  referenceKey: ReferenceKey | null;
  /** Whether the connection is being loaded */
  isLoading: boolean;
  /** Error message if connection failed */
  error?: string;
  /** Whether the connection is valid */
  isValid: boolean;
}

/**
 * Available connection for selection
 */
export interface AvailableConnection {
  /** Connection resource ID */
  id: string;
  /** Connection resource name (identifier used in API calls and workflow references) */
  name: string;
  /** Human-readable display name for UI */
  displayName?: string;
  /** Connector/API ID */
  connectorId: string;
  /** Connection status */
  status: ConnectionStatus;
  /** Created By */
  createdBy?: string;
  /** Creation timestamp */
  createdTime?: string;
  /** Last modified timestamp */
  lastModifiedTime?: string;
  properties?: {
    parameterValueSet?: {
      name: string;
      values: Record<string, unknown>;
    };
    customParameterValues?: Record<string, unknown>;
  };
}

export const ConnectionStatus = {
  Connected: 'connected',
  Error: 'error',
  Unauthenticated: 'unauthenticated',
} as const;
export type ConnectionStatus = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

/**
 * Connection feature type - determines special connection behaviors
 */
export const ConnectionFeatureType = {
  /** Dynamic connection that uses On-Behalf-Of (oBO) authentication at runtime */
  DynamicUserInvoked: 'DynamicUserInvoked',
} as const;
export type ConnectionFeatureType = (typeof ConnectionFeatureType)[keyof typeof ConnectionFeatureType];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate a unique reference key for a new connection
 */
export function generateReferenceKey(
  existingKeys: string[],
  connectorName: string
): string {
  const baseName = connectorName
    .split('/')
    .pop()
    ?.replace(/[^a-zA-Z0-9]/g, '') || 'connection';

  let key = baseName;
  let counter = 1;

  while (existingKeys.includes(key)) {
    key = `${baseName}_${counter}`;
    counter++;
  }

  return key;
}

/**
 * Check if a node has a valid connection
 */
export function hasValidConnection(
  mapping: ConnectionMapping,
  references: ConnectionReferences,
  nodeId: NodeId
): boolean {
  const refKey = mapping[nodeId];
  if (!refKey) return false;
  return !!references[refKey];
}

/**
 * Check if a connection has dynamic/oBO features enabled
 */
export function isDynamicConnection(features?: string): boolean {
  if (!features) return false;
  return features.toLowerCase() === ConnectionFeatureType.DynamicUserInvoked.toLowerCase();
}

/**
 * Check if a connection reference is using dynamic runtime
 */
export function isConnectionDynamic(reference: ConnectionReference): boolean {
  return reference.runtimeSource === ConnectionRuntimeSource.Dynamic;
}

/**
 * Get the connection reference for a node
 */
export function getNodeConnection(
  mapping: ConnectionMapping,
  references: ConnectionReferences,
  nodeId: NodeId
): ConnectionReference | undefined {
  const refKey = mapping[nodeId];
  if (!refKey) return undefined;
  return references[refKey];
}
